<template>
    <v-card>
        <v-card-title> Add Athlete </v-card-title>
        <v-card-text>
            <v-alert
                v-if="Object.keys(formErrors).length > 0"
                border="left"
                color="error"
                icon="mdi-alert-circle-outline"
                dense
                type="error"
            >
                <!-- Loop over the formErrors object and display the errors -->
                <ul>
                    <li v-for="(errors, field) in formErrors" :key="field">
                        <strong>{{ capitalCase(field) }}:</strong>
                        <ul>
                            <li v-for="error in errors" :key="error">{{ error }}</li>
                        </ul>
                    </li>
                </ul>
            </v-alert>

            <v-alert
                v-if="errorResponse"
                border="left"
                color="error"
                icon="mdi-alert-circle-outline"
                dense
                type="error"
            >
                {{ errorResponse.message }}
            </v-alert>

            <v-form>
                <v-row>
                    <v-col class="mx-auto text-center">
                        <scorsync-avatar
                            :person="form"
                            :initials="initials"
                            :avatar-color="avatarColor"
                            size="md"
                            new-athlete
                            @update="setAvatar"
                        />
                    </v-col>
                    <v-col>
                        <v-text-field
                            v-model="form.first_name"
                            :error="!!formErrors.first_name"
                            :error-messages="formErrors.first_name"
                            label="First Name"
                            required
                        />
                    </v-col>
                    <v-col>
                        <v-text-field
                            v-model="form.last_name"
                            :error="!!formErrors.last_name"
                            :error-messages="formErrors.last_name"
                            label="Last Name"
                            required
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-text-field
                            v-model="form.usag_number"
                            :error="!!formErrors.usag_number"
                            :error-messages="formErrors.usag_number"
                            label="USAG #"
                            required
                        ></v-text-field>
                    </v-col>
                    <v-col>
                        <v-select
                            v-model="form.gender"
                            :items="[
                                { value: 'M', text: 'Male' },
                                { value: 'F', text: 'Female' },
                            ]"
                            :error="!!formErrors.gender"
                            :error-messages="formErrors.gender"
                            label="Gender"
                            required
                        />
                    </v-col>
                    <v-col>
                        <v-menu
                            ref="menu"
                            v-model="menu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                        >
                            <template #activator="{ on, attrs }">
                                <v-text-field
                                    v-model="form.birthdate"
                                    :error="!!formErrors.birthdate"
                                    :error-messages="formErrors.birthdate"
                                    label="Birthdate"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                ></v-text-field>
                            </template>
                            <v-date-picker v-model="form.birthdate" no-title scrollable :max="yesterday" />
                        </v-menu>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-select
                            v-model="form.tra_level"
                            :items="levels"
                            :error="!!formErrors.tra_level"
                            :error-messages="formErrors.tra_level"
                            label="Trampoline Level"
                            color="blue"
                            item-text="short"
                            item-value="short"
                        />
                    </v-col>

                    <v-col>
                        <v-select
                            v-model="form.tum_level"
                            :items="levels"
                            :error="!!formErrors.tum_level"
                            :error-messages="formErrors.tum_level"
                            label="Tumbling Level"
                            color="red"
                            item-text="short"
                            item-value="short"
                        />
                    </v-col>

                    <v-col>
                        <v-select
                            v-model="form.dmt_level"
                            :items="levels"
                            :error="!!formErrors.dmt_level"
                            :error-messages="formErrors.dmt_level"
                            label="Double Mini Level"
                            color="green"
                            item-text="short"
                            item-value="short"
                        />
                    </v-col>
                </v-row>

                <v-row>
                    <v-col>
                        <v-checkbox
                            v-model="form.us_citizen"
                            :error="!!formErrors.us_citizen"
                            :error-messages="formErrors.us_citizen"
                            label="US Citizen"
                        ></v-checkbox>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="$emit('close')">Cancel</v-btn>
            <v-btn outlined color="success" :disabled="!isSubmittable" :loading="submitting" @click="submit(false)">
                Save and Add Another
            </v-btn>
            <v-btn color="success" :disabled="!isSubmittable" :loading="submittingOne" @click="submit">Save</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import isEmpty from 'lodash/isEmpty.js';
import { capitalCase } from 'change-case';
import Club from '@/Models/Club.js';
import levels from '@/levels.js';
import ScorsyncAvatar from '@/Components/ScorsyncAvatar.vue';
import { avatarColor, blobToBase64 } from '@/helpers.js';
import Athlete from '@/Models/Athlete.js';

export default {
    name: 'AddAthleteModal',
    components: { ScorsyncAvatar },

    props: {
        club: {
            type: Club,
            required: true,
        },
    },

    emits: ['close', 'added'],

    data() {
        return {
            levels,

            menu: false,
            submitting: false,
            submittingOne: false,
            yesterday: new Date(new Date().setDate(new Date().getDate() - 1)).toISOString().substr(0, 10),

            form: {},
            formErrors: {},
            errorResponse: null,

            snackbar: false,
            snackbarMessage: '',
        };
    },

    computed: {
        initials() {
            return (this.form.first_name?.charAt(0) || '') + (this.form.last_name?.charAt(0) || '');
        },

        avatarColor() {
            return avatarColor(this.form.first_name, this.form.last_name);
        },

        isSubmittable() {
            return (
                !isEmpty(this.form.first_name) &&
                !isEmpty(this.form.last_name) &&
                !isEmpty(this.form.usag_number) &&
                !isEmpty(this.form.birthdate) &&
                !isEmpty(this.form.gender) &&
                (!isEmpty(this.form.tra_level) || !isEmpty(this.form.dmt_level) || !isEmpty(this.form.tum_level))
            );
        },
    },

    created() {
        this.reset();
    },

    methods: {
        capitalCase,

        async submit(shouldClose = true) {
            this.formErrors = {};
            this.errorResponse = null;

            try {
                const formData = new FormData();

                Object.keys(this.form).forEach(key => {
                    if (this.form[key] !== null && this.form[key] !== undefined) {
                        formData.append(key, this.form[key]);
                    }
                });

                formData.append('club_id', this.club.id);
                formData.append('us_citizen', this.form.us_citizen ? 1 : 0);

                if (shouldClose) {
                    this.submittingOne = true;
                } else {
                    this.submitting = true;
                }

                await Athlete.api().post('/api/athletes', formData);

                this.$emit('added', this.form);

                this.reset();

                if (shouldClose) {
                    this.$emit('close');
                }
            } catch (error) {
                if (error.response && error.response.status === 422) {
                    // Handle validation errors
                    this.formErrors = error.response.data.errors;
                } else if (error.response && error.response.status === 403) {
                    this.errorResponse = error.response.data;
                } else {
                    // Handle other errors
                    console.error(error);
                }
            } finally {
                this.submittingOne = false;
                this.submitting = false;
            }
        },

        async setAvatar({ blob }) {
            this.form.avatar = blob;
            this.form.avatar_b64 = await blobToBase64(blob);
        },

        reset() {
            this.form = {
                avatar: null,
                avatar_b64: null,
                first_name: '',
                last_name: '',
                usag_number: null,
                gender: 'F',
                birthdate: null,
                tra_level: '',
                dmt_level: '',
                tum_level: '',
                us_citizen: true,
            };
        },
    },
};
</script>
